import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Coffee,
  Sun,
  Moon,
  Cookie,
  Utensils,
  Target,
  TrendingUp,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Flame,
  Beef,
  Wheat,
  Droplet
} from 'lucide-react';

const MEAL_TYPES = [
  { id: 'breakfast', label: 'Breakfast', icon: Coffee, time: '7:00 AM' },
  { id: 'lunch', label: 'Lunch', icon: Sun, time: '12:00 PM' },
  { id: 'dinner', label: 'Dinner', icon: Moon, time: '6:00 PM' },
  { id: 'snack', label: 'Snacks', icon: Cookie, time: 'Any time' }
];

const QUICK_FOODS = [
  { name: 'Chicken Breast', portion: '100g', calories: 165, protein_g: 31, carbs_g: 0, fats_g: 3.6 },
  { name: 'Brown Rice', portion: '1 cup', calories: 216, protein_g: 5, carbs_g: 45, fats_g: 1.8 },
  { name: 'Egg', portion: '1 large', calories: 72, protein_g: 6, carbs_g: 0.4, fats_g: 5 },
  { name: 'Greek Yogurt', portion: '1 cup', calories: 100, protein_g: 17, carbs_g: 6, fats_g: 0.7 },
  { name: 'Banana', portion: '1 medium', calories: 105, protein_g: 1.3, carbs_g: 27, fats_g: 0.4 },
  { name: 'Oatmeal', portion: '1 cup', calories: 150, protein_g: 5, carbs_g: 27, fats_g: 2.5 },
  { name: 'Salmon', portion: '100g', calories: 208, protein_g: 20, carbs_g: 0, fats_g: 13 },
  { name: 'Avocado', portion: '1/2', calories: 160, protein_g: 2, carbs_g: 9, fats_g: 15 },
  { name: 'Protein Shake', portion: '1 scoop', calories: 120, protein_g: 24, carbs_g: 3, fats_g: 1 },
  { name: 'Sweet Potato', portion: '1 medium', calories: 103, protein_g: 2, carbs_g: 24, fats_g: 0 }
];

export default function FoodLoggingPage() {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [foodLogs, setFoodLogs] = useState({});
  const [dailySummary, setDailySummary] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addFoodOpen, setAddFoodOpen] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('breakfast');
  
  // Custom food form
  const [customFood, setCustomFood] = useState({
    name: '',
    portion: '1 serving',
    calories: 0,
    protein_g: 0,
    carbs_g: 0,
    fats_g: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const [logsRes, summaryRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/nutrition/log/${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/nutrition/summary/${selectedDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/nutrition/weekly`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Group logs by meal type
      const grouped = {};
      logsRes.data.forEach(log => {
        grouped[log.meal_type] = log;
      });
      setFoodLogs(grouped);
      setDailySummary(summaryRes.data);
      setWeeklyData(weeklyRes.data);
    } catch (error) {
      console.error('Error fetching nutrition data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addQuickFood = async (food) => {
    try {
      await axios.post(
        `${API}/nutrition/quick-add`,
        null,
        {
          params: {
            date: selectedDate,
            meal_type: selectedMealType,
            ...food
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Added ${food.name}`);
      fetchData();
      setAddFoodOpen(false);
    } catch (error) {
      toast.error('Failed to add food');
    }
  };

  const addCustomFood = async () => {
    if (!customFood.name || customFood.calories <= 0) {
      toast.error('Please enter food name and calories');
      return;
    }
    
    await addQuickFood(customFood);
    setCustomFood({
      name: '',
      portion: '1 serving',
      calories: 0,
      protein_g: 0,
      carbs_g: 0,
      fats_g: 0
    });
  };

  const deleteFood = async (logId, foodIndex) => {
    try {
      const log = Object.values(foodLogs).find(l => l.id === logId);
      if (!log) return;
      
      const updatedFoods = log.foods.filter((_, i) => i !== foodIndex);
      
      if (updatedFoods.length === 0) {
        await axios.delete(`${API}/nutrition/log/${logId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(
          `${API}/nutrition/log/${logId}`,
          null,
          {
            params: { foods: JSON.stringify(updatedFoods) },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
      
      toast.success('Food removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove food');
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const MacroCard = ({ label, consumed, target, color, icon: Icon }) => {
    const percentage = target > 0 ? Math.min((consumed / target) * 100, 100) : 0;
    return (
      <div className="bg-secondary/50 p-4 rounded-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${color}`} />
            <span className="text-sm text-gray-400">{label}</span>
          </div>
          <span className="text-sm text-white font-mono">
            {Math.round(consumed)}/{Math.round(target)}g
          </span>
        </div>
        <Progress value={percentage} className="h-2" />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="food-logging-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            <span className="text-primary">NUTRITION</span> LOG
          </h1>
          <p className="text-gray-400 mt-1">Track your daily meals and macros</p>
        </div>
      </div>

      {/* Date Selector */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => changeDate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="text-lg text-white font-medium min-w-[150px] text-center">
          {formatDate(selectedDate)}
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => changeDate(1)}
          disabled={selectedDate >= new Date().toISOString().split('T')[0]}
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Daily Summary */}
      {dailySummary && (
        <div className="card-tactical">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg text-white font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Daily Progress
            </h2>
            <span className="text-sm text-gray-400">
              {dailySummary.meals_logged} meals logged
            </span>
          </div>

          {/* Calories Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="text-white font-medium">Calories</span>
              </div>
              <div className="text-right">
                <span className="text-2xl font-mono text-white">{dailySummary.consumed.calories}</span>
                <span className="text-gray-400"> / {dailySummary.targets.calories}</span>
              </div>
            </div>
            <Progress 
              value={dailySummary.percentage.calories} 
              className="h-3"
            />
            <div className="flex justify-between mt-1 text-sm">
              <span className="text-gray-400">{dailySummary.remaining.calories} remaining</span>
              <span className="text-primary">{dailySummary.percentage.calories}%</span>
            </div>
          </div>

          {/* Macros */}
          <div className="grid grid-cols-3 gap-4">
            <MacroCard 
              label="Protein" 
              consumed={dailySummary.consumed.protein}
              target={dailySummary.targets.protein}
              color="text-red-500"
              icon={Beef}
            />
            <MacroCard 
              label="Carbs" 
              consumed={dailySummary.consumed.carbs}
              target={dailySummary.targets.carbs}
              color="text-yellow-500"
              icon={Wheat}
            />
            <MacroCard 
              label="Fats" 
              consumed={dailySummary.consumed.fats}
              target={dailySummary.targets.fats}
              color="text-blue-500"
              icon={Droplet}
            />
          </div>
        </div>
      )}

      {/* Meals */}
      <div className="space-y-4">
        {MEAL_TYPES.map((meal) => {
          const log = foodLogs[meal.id];
          const Icon = meal.icon;
          
          return (
            <div key={meal.id} className="card-tactical">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{meal.label}</h3>
                    <p className="text-xs text-gray-400">{meal.time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400">
                    {log ? `${log.total_calories} cal` : '0 cal'}
                  </span>
                  <Dialog open={addFoodOpen && selectedMealType === meal.id} onOpenChange={(open) => {
                    setAddFoodOpen(open);
                    if (open) setSelectedMealType(meal.id);
                  }}>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-primary text-primary hover:bg-primary hover:text-white"
                        onClick={() => setSelectedMealType(meal.id)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border max-w-lg max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-white">Add Food to {meal.label}</DialogTitle>
                      </DialogHeader>
                      
                      <Tabs defaultValue="quick" className="mt-4">
                        <TabsList className="bg-secondary/50 w-full">
                          <TabsTrigger value="quick" className="flex-1">Quick Add</TabsTrigger>
                          <TabsTrigger value="custom" className="flex-1">Custom</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="quick" className="mt-4">
                          <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                            {QUICK_FOODS.map((food, i) => (
                              <button
                                key={i}
                                onClick={() => addQuickFood(food)}
                                className="p-3 bg-secondary/50 hover:bg-secondary rounded-sm text-left transition-colors"
                              >
                                <div className="text-white text-sm font-medium">{food.name}</div>
                                <div className="text-xs text-gray-400">{food.portion}</div>
                                <div className="text-xs text-primary mt-1">{food.calories} cal</div>
                              </button>
                            ))}
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="custom" className="mt-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <Label className="text-gray-300">Food Name</Label>
                              <Input
                                value={customFood.name}
                                onChange={(e) => setCustomFood({...customFood, name: e.target.value})}
                                placeholder="e.g., Grilled Chicken"
                                className="bg-secondary border-transparent mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Portion</Label>
                              <Input
                                value={customFood.portion}
                                onChange={(e) => setCustomFood({...customFood, portion: e.target.value})}
                                placeholder="e.g., 100g"
                                className="bg-secondary border-transparent mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Calories</Label>
                              <Input
                                type="number"
                                value={customFood.calories}
                                onChange={(e) => setCustomFood({...customFood, calories: parseInt(e.target.value) || 0})}
                                className="bg-secondary border-transparent mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Protein (g)</Label>
                              <Input
                                type="number"
                                value={customFood.protein_g}
                                onChange={(e) => setCustomFood({...customFood, protein_g: parseFloat(e.target.value) || 0})}
                                className="bg-secondary border-transparent mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Carbs (g)</Label>
                              <Input
                                type="number"
                                value={customFood.carbs_g}
                                onChange={(e) => setCustomFood({...customFood, carbs_g: parseFloat(e.target.value) || 0})}
                                className="bg-secondary border-transparent mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Fats (g)</Label>
                              <Input
                                type="number"
                                value={customFood.fats_g}
                                onChange={(e) => setCustomFood({...customFood, fats_g: parseFloat(e.target.value) || 0})}
                                className="bg-secondary border-transparent mt-1"
                              />
                            </div>
                          </div>
                          <Button onClick={addCustomFood} className="w-full bg-primary hover:bg-red-700">
                            Add Food
                          </Button>
                        </TabsContent>
                      </Tabs>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Food List */}
              {log && log.foods && log.foods.length > 0 ? (
                <div className="space-y-2">
                  {log.foods.map((food, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                      <div>
                        <div className="text-white text-sm">{food.name}</div>
                        <div className="text-xs text-gray-400">{food.portion}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-white">{food.calories} cal</div>
                          <div className="text-xs text-gray-400">
                            P:{food.protein_g}g C:{food.carbs_g}g F:{food.fats_g}g
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteFood(log.id, i)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400 text-sm">
                  No foods logged yet
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Weekly Chart */}
      {weeklyData.length > 0 && (
        <div className="card-tactical">
          <h3 className="text-lg text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Weekly Overview
          </h3>
          <div className="flex items-end justify-between h-32 gap-2">
            {weeklyData.slice().reverse().map((day, i) => {
              const target = dailySummary?.targets?.calories || 2000;
              const height = Math.min((day.calories / target) * 100, 100);
              const isToday = day.date === selectedDate;
              
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className={`w-full rounded-t-sm transition-all ${
                      isToday ? 'bg-primary' : 'bg-secondary'
                    }`}
                    style={{ height: `${height}%`, minHeight: day.calories > 0 ? '8px' : '2px' }}
                  />
                  <span className="text-xs text-gray-400">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                  <span className="text-xs text-white font-mono">{day.calories}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
